import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { CircleCheck, CreditCard, Shield, Truck } from "lucide-react";
const summaryItems = [{
  icon: Shield,
  text: "Data Privacy Protection"
}, {
  icon: Truck,
  text: "Secure Digital Delivery"
}, {
  icon: CircleCheck,
  text: "Compliance Guaranteed"
}, {
  icon: CreditCard,
  text: "Transparent Billing"
}];
const policies = [{
  id: "services-provided",
  title: "1. Services Provided",
  content: <div className="max-w-2xl space-y-4 leading-relaxed text-foreground/80">
        <p className="font-sans">
          GTMX Labs provides Timber Cruise Analysis (TCA) reports generated through the processing of aerial LiDAR data using proprietary machine learning algorithms. Our services include:
        </p>
        <ul className="list-disc pl-5 space-y-2 font-sans">
          <li>Individual tree detection and measurement (DBH, height)</li>
          <li>Species identification and distribution mapping</li>
          <li>Merchantable volume estimation by product class</li>
          <li>Digital stand maps and geospatial data exports</li>
        </ul>
      </div>
}, {
  id: "not-substitute",
  title: "2. Not a Substitute for Professional Appraisal",
  content: <div className="max-w-2xl space-y-4 leading-relaxed text-foreground/80">
        <p className="font-sans">
          While our TCA reports provide high-precision data, they are intended as decision-support tools. They do not constitute a formal real estate appraisal or a guaranteed financial valuation. Users should consult with registered foresters or licensed appraisers for statutory requirements.
        </p>
      </div>
}, {
  id: "no-warranties",
  title: "3. No Warranties",
  content: <div className="max-w-2xl space-y-4 leading-relaxed text-foreground/80">
        <p className="font-sans">
          Services are provided "as is" and "as available." GTMX Labs makes no warranties, expressed or implied, regarding the absolute accuracy of biological growth models or hidden defects in timber that cannot be detected via remote sensing.
        </p>
      </div>
}, {
  id: "limitation-liability",
  title: "4. Limitation of Liability",
  content: <div className="max-w-2xl space-y-4 leading-relaxed text-foreground/80">
        <p className="font-sans">
          In no event shall GTMX Labs be liable for any indirect, consequential, or incidental damages arising out of the use of our reports. Our total liability for any claim shall not exceed the amount paid by the customer for the specific TCA report in question.
        </p>
      </div>
}, {
  id: "payment-terms",
  title: "5. Payment Terms",
  content: <div className="max-w-2xl space-y-4 leading-relaxed text-foreground/80">
        <p className="font-sans">
          Payment is required in full prior to the commencement of data processing. For large-scale TIMO projects, custom billing cycles may be established via separate Master Service Agreements (MSA).
        </p>
      </div>
}, {
  id: "intellectual-property",
  title: "6. Intellectual Property",
  content: <div className="max-w-2xl space-y-4 leading-relaxed text-foreground/80">
        <p className="font-sans">
          GTMX Labs retains all rights to its proprietary AI models, processing workflows, and software. Customers are granted a non-exclusive, perpetual license to use the final TCA reports for their internal business purposes or timber sale documentation.
        </p>
      </div>
}, {
  id: "use-of-website",
  title: "7. Use of Website",
  content: <div className="max-w-2xl space-y-4 leading-relaxed text-foreground/80">
        <p className="font-sans">
          Users agree not to attempt to reverse-engineer our platform, bypass security measures, or use automated scrapers to extract data from our public-facing interfaces.
        </p>
      </div>
}, {
  id: "modifications",
  title: "8. Modifications to Terms",
  content: <div className="max-w-2xl space-y-4 leading-relaxed text-foreground/80">
        <p className="font-sans">
          We reserve the right to update these terms at any time. Changes will be effective immediately upon posting to this page. Continued use of our services constitutes acceptance of the revised terms.
        </p>
      </div>
}, {
  id: "governing-law",
  title: "9. Governing Law",
  content: <div className="max-w-2xl space-y-4 leading-relaxed text-foreground/80">
        <p className="font-sans">
          These terms are governed by the laws of the state in which GTMX Labs is incorporated, without regard to conflict of law principles. Any disputes shall be resolved in the appropriate state or federal courts.
        </p>
      </div>
}, {
  id: "contact-information",
  title: "10. Contact Information",
  content: <div className="max-w-2xl space-y-4 leading-relaxed text-foreground/80">
        <p className="font-sans">
          For legal inquiries or formal notices, please contact our compliance department at:
        </p>
        <p className="font-sans font-medium">
          legal@gtmxlabs.com
        </p>
        <p className="font-sans">
          Or visit our{" "}
          <Link to="/ContactPage" className="text-primary hover:underline font-medium">
            Contact Page
          </Link>{" "}
          to submit a formal request.
        </p>
      </div>
}];
export default function TheTransparencyReport() {
  return <section data-section-id="224310" id="terms-of-service" className="w-full bg-background py-16 md:py-24">
      <div className="container mx-auto px-4 md:px-6 max-w-6xl">
        <div className="mb-12">
          <h2 className="font-default text-3xl md:text-4xl font-bold tracking-tight text-foreground">
            Terms of Service & Legal
          </h2>
          <p className="mt-4 text-lg text-foreground/70 max-w-2xl font-sans">
            Our commitment to clarity and professional integrity. We provide detailed documentation of our operational standards to ensure a secure partnership.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-10 lg:gap-16 items-start">
          <aside className="border border-border bg-card p-6 lg:sticky lg:top-24 rounded-md shadow-sm">
            <h3 className="font-default text-lg font-semibold text-foreground mb-6">
              Compliance Summary
            </h3>
            <ul className="space-y-5">
              {summaryItems.map((item, i) => {
              const Icon = item.icon;
              return <li key={i} data-index={i} className="flex items-start gap-3">
                    <Icon className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                    <span className="text-sm font-medium text-foreground/80 font-sans">
                      {item.text}
                    </span>
                  </li>;
            })}
            </ul>
            <div className="mt-8 pt-6 border-t border-border">
              <p className="text-sm text-foreground/60 mb-4 font-sans">
                Have specific legal questions? Our compliance team is ready to assist.
              </p>
              <Link to="/ContactPage" className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all bg-primary text-primary-foreground shadow-md hover:bg-primary/90 h-9 px-4 py-2 w-full font-default">
                Contact Legal Team
              </Link>
            </div>
          </aside>

          <div className="w-full">
            <Accordion type="single" collapsible className="w-full" defaultValue="services-provided">
              {policies.map((policy, i) => <AccordionItem key={policy.id} value={policy.id} data-index={i} className="border-border">
                  <AccordionTrigger className="font-default text-lg font-semibold hover:no-underline hover:text-primary transition-colors py-6">
                    {policy.title}
                  </AccordionTrigger>
                  <AccordionContent className="pb-6">
                    {policy.content}
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>
        </div>
      </div>
    </section>;
}